const formatDateLabel = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDateRange = (start, end) => {
  const from = formatDateLabel(start);
  const to = formatDateLabel(end);
  if (from && to) return `${from} – ${to}`;
  return from || to || null;
};

const warehouseDisplayName = (warehouse, fallback = "the selected warehouse") => {
  if (!warehouse) return fallback;
  if (typeof warehouse === "string") return warehouse;
  return (
    warehouse.warehouse_name ||
    warehouse.name ||
    warehouse.warehouse_code ||
    warehouse.warehouse_id ||
    fallback
  );
};

const EXCLUSION_LABELS = {
  outside_sales_source_coverage: "outside sales coverage",
  missing_inventory: "missing inventory",
  insufficient_demand_history: "insufficient demand history",
};

const summarizeExclusions = (exclusionSummary = {}) =>
  Object.entries(exclusionSummary)
    .filter(([, count]) => count > 0)
    .map(([reason, count]) => `${count} ${EXCLUSION_LABELS[reason] || reason}`)
    .join(", ");

export const isLostSalesErrorPayload = (payload) =>
  Boolean(
    payload &&
      typeof payload === "object" &&
      payload.status !== "complete" &&
      payload.code != null &&
      (payload.name || payload.message),
  );

export const getLostSalesErrorPresentation = (error, inventoryContext = {}) => {
  const payload = error?.response?.data ||
    (isLostSalesErrorPayload(error) ? error : {});
  const status =
    error?.response?.status && error.response.status !== 200
      ? error.response.status
      : payload.code;
  const data = payload.data || {};
  const code = data.code;
  const backendMessage = String(payload.message || "").toLowerCase();
  const warehouseName =
    data.warehouse_name ||
    warehouseDisplayName(inventoryContext?.warehouse) ||
    "the selected warehouse";
  const requestedRange = formatDateRange(
    data.start_date || inventoryContext?.startDate,
    data.end_date || inventoryContext?.endDate,
  );
  const salesRange = formatDateRange(
    data.sales_coverage?.start,
    data.sales_coverage?.end,
  );

  if (code === "NO_WAREHOUSE_SALES_COVERAGE") {
    const inventoryNote =
      data.inventory_days_in_period > 0
        ? `Inventory data is available for ${warehouseName}`
        : `No inventory snapshots were found for ${warehouseName} in the selected period`;
    const periodNote = requestedRange
      ? ` during ${requestedRange}`
      : " during the selected period";
    return {
      title: "No sales data for this warehouse",
      body: `${inventoryNote}, but no orders were found for this warehouse${periodNote}. Lost Sales cannot be calculated without warehouse-specific sales data.`,
    };
  }

  if (code === "PERIOD_OUTSIDE_SALES_COVERAGE") {
    const coverageNote = salesRange
      ? ` Available warehouse sales history: ${salesRange}.`
      : "";
    return {
      title: "Date range outside sales history",
      body: `The selected date range${requestedRange ? ` (${requestedRange})` : ""} falls outside the available order history for ${warehouseName}.${coverageNote}`,
    };
  }

  if (code === "NO_INVENTORY_IN_PERIOD") {
    return {
      title: "No inventory history for this period",
      body: `No historical inventory was found for the selected product at ${warehouseName}${requestedRange ? ` during ${requestedRange}` : " during the selected period"}.`,
    };
  }

  if (code === "NO_ANALYZABLE_DAYS") {
    const exclusionText = summarizeExclusions(data.exclusion_summary);
    return {
      title: "No analyzable days in this period",
      body: exclusionText
        ? `No days in the selected period could be analyzed for ${warehouseName}. Excluded days: ${exclusionText}.`
        : `No days in the selected period could be analyzed for ${warehouseName}. Inventory and sales data did not overlap enough to build a valid demand window.`,
    };
  }

  if (status === 422) {
    if (backendMessage.includes("historical sales coverage")) {
      return {
        title: "No sales data for this warehouse",
        body: `No warehouse-specific order history was found for ${warehouseName}. Lost Sales cannot be calculated without sales data for the selected warehouse.`,
      };
    }
    if (
      backendMessage.includes("outside") &&
      backendMessage.includes("sales coverage")
    ) {
      return {
        title: "Date range outside sales history",
        body: `The selected date range falls outside the available order history for ${warehouseName}.`,
      };
    }
    if (backendMessage.includes("historical inventory")) {
      return {
        title: "No inventory history for this period",
        body: `No historical inventory is available for the selected product at ${warehouseName} during the selected period.`,
      };
    }
    if (backendMessage.includes("valid analyzable days")) {
      return {
        title: "No analyzable days in this period",
        body: `No days in the selected period could be analyzed for ${warehouseName}.`,
      };
    }
    return {
      title: "Insufficient historical data",
      body: "There isn't enough historical data available for the selected date range. Please choose a different analysis period.",
    };
  }

  if (status === 400) {
    if (code === "BULK_LIMIT_EXCEEDED") {
      if (data.limit === "target_date_range_days") {
        const requested = data.requested;
        const maximum = data.maximum;
        return {
          title: "Invalid date range",
          body:
            requested != null && maximum != null
              ? `Date range of ${requested} days exceeds the maximum of ${maximum} days.`
              : payload.message ||
                "Please choose a valid analysis date range and try again.",
        };
      }
      return {
        title: "Selection too large",
        body:
          payload.message ||
          "The selection exceeds Lost Sales bulk limits. Please reduce the selection and try again.",
      };
    }

    if (backendMessage.includes("sku is required")) {
      return {
        title: "SKU selection required",
        body: "SKU selection is required for this client.",
      };
    }

    // Genuine date validation only — do not match arbitrary "required"/"date" text.
    const isDateFormatError = backendMessage.includes("yyyy-mm-dd");
    const isDateOrderError = backendMessage.includes(
      "start_date must be on or before",
    );
    const isMissingDatesError =
      backendMessage.includes("start_date") &&
      backendMessage.includes("end_date") &&
      backendMessage.includes("required");
    if (isDateFormatError || isDateOrderError || isMissingDatesError) {
      return {
        title: "Invalid date range",
        body: "Please choose a valid analysis date range and try again.",
      };
    }

    return {
      title: "Invalid parameters",
      body:
        payload.message ||
        "The selected analysis parameters could not be processed. Please review them and try again.",
    };
  }

  if (status === 403) {
    return {
      title: "Access denied",
      body: "The selected product context is outside your client scope.",
    };
  }

  return {
    title: "Analysis failed",
    body: "Unable to run the analysis right now. Please try again.",
  };
};

export const summarizeLostSalesExclusions = (excluded = []) => {
  const counts = excluded.reduce((acc, row) => {
    const reason = row.exclusion_reason;
    if (!reason) return acc;
    acc[reason] = (acc[reason] || 0) + 1;
    return acc;
  }, {});
  return summarizeExclusions(counts);
};
